import { TabList, TabPanel, TabPanels, Tabs, TabsProps } from '@chakra-ui/react';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { AiMarkdown } from '@/components/ui/features/lecture/AiMarkdowns/AiMarkdown';
import { useLecturePageQueryParams } from '@/components/ui/features/lecture/hooks';
import { Tab } from '@/components/ui/features/lecture/LectureTabs/LectureTabs.elements';
import { ChatState } from '@/enums/ChatState';
import { queryArticlesByLectureId } from '@/fetchers/articles';
import { queryKeywordsByLectureId } from '@/fetchers/keywords';
import { queryAllLectureProfilesByLectureId } from '@/fetchers/lectureProfiles';
import { queryThinLectureWithTagAndEvent } from '@/fetchers/thinLectures';
import { useIsClientSide } from '@/hooks/useIsClientSide';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useResource, useResources } from '@/lib/datx-jsonapi-react';
import { Article } from '@/resources/Article';
import { getSubdomain } from '@/utils/subdomain-matcher';

export interface ILectureTabsProps extends Omit<TabsProps, 'children'> {
  discussion?: ReactNode;
  abstract?: ReactNode;
  aboutAuthors?: ReactNode;
  learnMore?: ReactNode;
}

export const LectureTabs: FC<ILectureTabsProps> = ({ discussion, abstract, aboutAuthors, learnMore, ...rest }) => {
  const { t } = useTranslation();
  const { id: lectureId } = useLecturePageQueryParams();
  const isClientSide = useIsClientSide();

  const { data: lecture } = useResource(queryThinLectureWithTagAndEvent(lectureId));
  const { data: keywords } = useResources(queryKeywordsByLectureId(lecture.id));
  const { data: articles } = useResources<Article>(queryArticlesByLectureId(lectureId));
  const { data: lectureProfiles } = useResources(queryAllLectureProfilesByLectureId(lectureId));

  const isLibraryChatState = lecture?.chatState === ChatState.Library;
  const isAbstractVisible = Boolean(lecture?.abstract || keywords?.length !== 0);
  const isLearnMoreVisible = Boolean(articles?.length);
  const isAboutAuthorsVisible = Boolean(lectureProfiles?.length);

  const host = isClientSide ? window.location.host : '';
  const isAISubdomain = getSubdomain(host) === 'ai';

  if (!lecture) {
    return null;
  }

  if (!isLibraryChatState && !isAbstractVisible && !isLearnMoreVisible && !isAboutAuthorsVisible) {
    return null;
  }

  return (
    <Tabs size="md" isLazy {...rest}>
      <TabList>
        {isLibraryChatState ? <Tab>{t('newLecture.tabs.discussion')}</Tab> : null}
        {isAbstractVisible ? <Tab>{t('newLecture.tabs.abstract')}</Tab> : null}
        {isAboutAuthorsVisible ? <Tab>{t('newLecture.tabs.aboutAuthors')}</Tab> : null}
        {isLearnMoreVisible ? <Tab>{t('newLecture.tabs.learnMore')}</Tab> : null}
        {isAISubdomain && lecture?.aiSummary ? <Tab>{t('newLecture.tabs.aiSummary')}</Tab> : null}
        {isAISubdomain && lecture?.aiInsights ? <Tab>{t('newLecture.tabs.aiInsights')}</Tab> : null}
        {isAISubdomain && lecture?.aiQuestionsAnswers ? <Tab>{t('newLecture.tabs.aiQa')}</Tab> : null}
      </TabList>
      <TabPanels>
        {isLibraryChatState ? <TabPanel>{discussion}</TabPanel> : null}
        {isAbstractVisible ? <TabPanel>{abstract}</TabPanel> : null}
        {isAboutAuthorsVisible ? <TabPanel>{aboutAuthors}</TabPanel> : null}
        {isLearnMoreVisible ? <TabPanel>{learnMore}</TabPanel> : null}
        {lecture?.aiSummary && (
          <TabPanel>
            <AiMarkdown content={lecture?.aiSummary} />
          </TabPanel>
        )}
        {lecture?.aiInsights && (
          <TabPanel>
            <AiMarkdown content={lecture?.aiInsights} />
          </TabPanel>
        )}
        {lecture?.aiQuestionsAnswers && (
          <TabPanel>
            <AiMarkdown content={lecture?.aiQuestionsAnswers} />
          </TabPanel>
        )}
      </TabPanels>
    </Tabs>
  );
};
